import { GetIssueResponseDtoStatusEnum } from '@issuemanager/api-client';
import {
  useAtlasCoreThemeTokens,
  useAtlasSemanticThemeTokens,
} from '@issuemanager/apps/client/theme';
import type {
  GetLabelForStatusAndActionProgressParams,
  GetLabelForStatusAndActionProgressReturnType,
  IssueStatusData,
  UseIssueStatusesReturnType,
} from '@issuemanager/client/features/issues';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFeatureFlags } from '../../users';

const matchesStatusCriteria = (
  statusConfig: IssueStatusData,
  status: GetIssueResponseDtoStatusEnum,
  isInitiated?: boolean | null,
): boolean => {
  if (statusConfig.statusValue !== status) {
    return false;
  }

  if (
    statusConfig.isInitiatedValue === undefined ||
    statusConfig.isInitiatedValue === null
  ) {
    return true;
  }

  return isInitiated === statusConfig.isInitiatedValue;
};

export const useIssueStatuses = (): UseIssueStatusesReturnType => {
  const { t } = useTranslation(['common', 'issue']);
  const semantic = useAtlasSemanticThemeTokens();
  const core = useAtlasCoreThemeTokens();
  const whiteColor = core.color.legacy.white.value;
  const darkColor = semantic.color.text.body.value;

  const isDraftIssuesEnabled = useFeatureFlags('draftIssues');
  const isExtendedWorkflowEnabled = useFeatureFlags('issueExtendedWorkflow');

  const issueStatuses: IssueStatusData[] = useMemo(() => {
    const group = t('statusLabels.issue');
    const draftStatusData: Omit<IssueStatusData, 'id'> = {
      statusValue: GetIssueResponseDtoStatusEnum.Draft,
      label: t('issue:view.status.draft'),
      color: darkColor,
      backgroundColor: semantic.color.accent['05'].value,
      group,
    };

    const inProgressStatusData: Array<Omit<IssueStatusData, 'id'>> =
      isExtendedWorkflowEnabled
        ? [
            {
              statusValue: GetIssueResponseDtoStatusEnum.InProgress,
              isInitiatedValue: false,
              label: t('issue:view.status.remediationPlanning'),
              color: whiteColor,
              backgroundColor: semantic.color.link.default.value,
              group,
            },
            {
              statusValue: GetIssueResponseDtoStatusEnum.InProgress,
              isInitiatedValue: true,
              label: t('issue:view.status.inRemediation'),
              color: whiteColor,
              backgroundColor: semantic.color.link.default.value,
              group,
            },
            {
              statusValue: GetIssueResponseDtoStatusEnum.InProgress,
              label: t('issue:view.status.inProgress'),
              color: whiteColor,
              backgroundColor: semantic.color.link.default.value,
              group,
            },
          ]
        : [
            {
              statusValue: GetIssueResponseDtoStatusEnum.InProgress,
              label: t('issue:view.status.inProgress'),
              color: whiteColor,
              backgroundColor: semantic.color.link.default.value,
              group,
            },
          ];
    const standardStatusDataArray: Array<Omit<IssueStatusData, 'id'>> = [
      {
        statusValue: GetIssueResponseDtoStatusEnum.Open,
        label: isExtendedWorkflowEnabled
          ? t('issue:view.status.inReview')
          : t('issue:view.status.open'),
        color: darkColor,
        backgroundColor: semantic.color.status.warning.value,
        group,
      },
      ...inProgressStatusData,
      {
        statusValue: GetIssueResponseDtoStatusEnum.Closed,
        label: t('issue:view.status.closed'),
        color: whiteColor,
        backgroundColor: semantic.color.status.success.value,
        group,
      },
      {
        statusValue: GetIssueResponseDtoStatusEnum.Discarded,
        label: t('issue:view.status.discarded'),
        color: whiteColor,
        backgroundColor: semantic.color.text.muted.value,
        group,
      },
      {
        statusValue: GetIssueResponseDtoStatusEnum.ToBeApproved,
        label: t('issue:view.status.toBeApproved'),
        color: whiteColor,
        backgroundColor: semantic.color.link.default.value,
        group,
      },
    ];

    const allStatuses = [
      ...(isDraftIssuesEnabled ? [draftStatusData] : []),
      ...standardStatusDataArray,
    ];

    return allStatuses.map((status, index) => ({
      ...status,
      id: String(index + 1),
    }));
  }, [
    darkColor,
    semantic,
    whiteColor,
    isDraftIssuesEnabled,
    isExtendedWorkflowEnabled,
    t,
  ]);

  const getStatusDisplay = ({
    status,
    isInitiated,
  }: GetLabelForStatusAndActionProgressParams): GetLabelForStatusAndActionProgressReturnType =>
    issueStatuses.find((config) =>
      matchesStatusCriteria(config, status, isInitiated),
    ) ?? null;
  return {
    issueStatuses,
    getStatusDisplay,
  };
};
